// src/web/measurements.js
//
// Computes optical/eyewear measurements from MediaPipe face landmarks and the
// current goggle placement. All distances are returned in millimetres.
//
// The scale anchor is the iris. The human iris (the coloured disc, i.e. the
// visible limbus boundary) has a remarkably stable horizontal diameter of
// ~11.7 mm across the adult population, independent of ethnicity, sex, and
// (largely) age. MediaPipe's iris refinement gives us the iris landmark ring,
// so we can measure the iris in MediaPipe-normalised units and derive a
// pixels/normalised → millimetres scale factor. Everything else is measured in
// that same normalised space and converted via this factor.
//
// MEASUREMENTS
//
//   PD (Pupillary Distance)       — horizontal distance between the two pupil
//                                    centres. The single most important fitting
//                                    parameter for lens centration.
//
//   Monocular PD (L / R)          — distance from the nose bridge centreline to
//                                    each pupil. Often differs left vs right.
//
//   Segment Height                — vertical distance from the lowest point of
//                                    the lens (bottom of frame) up to the pupil
//                                    centre. Needed for progressive/bifocal
//                                    lenses. Depends on how the frame sits, so
//                                    it is computed from the goggle geometry +
//                                    pupil position.
//
//   Pantoscopic Tilt              — the angle of the lens plane relative to the
//                                    vertical (frontal) plane, i.e. how much the
//                                    bottom of the lens tips toward the cheek.
//                                    Derived from the frame's pitch (head-local
//                                    X rotation) plus any model tilt.
//
//   Vertex Distance               — the distance from the back surface of the
//                                    lens to the front of the cornea (eye). For
//                                    a virtual fit we approximate it from the
//                                    goggle's head-local Z offset to the corneal
//                                    apex landmark.
//
// IMPORTANT: these are ESTIMATES from a single monocular RGB camera. They are
// good for relative comparison and a guided self-measurement experience, not a
// substitute for an optician's calibrated measurement. We surface a confidence
// value with each.

import {
  LEFT_EYE_CENTER, RIGHT_EYE_CENTER,
  LEFT_EYE_OUTER, LEFT_EYE_INNER, RIGHT_EYE_INNER, RIGHT_EYE_OUTER,
  NOSE_BRIDGE_TOP, IRIS_DIAMETER_MM
} from './faceConstants.js';

// MediaPipe iris landmark rings (with iris refinement enabled, which the
// Face Landmarker .task model includes by default):
//   Left iris:  468 (centre), 469, 470, 471, 472 (ring)
//   Right iris: 473 (centre), 474, 475, 476, 477 (ring)
const LEFT_IRIS_RING  = [469, 470, 471, 472];
const RIGHT_IRIS_RING = [474, 475, 476, 477];

function dist3(a, b) {
  const dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
  return Math.sqrt(dx*dx + dy*dy + dz*dz);
}
function dist2(a, b) {
  const dx = a.x - b.x, dy = a.y - b.y;
  return Math.sqrt(dx*dx + dy*dy);
}

// Estimate the iris diameter in normalised units by averaging the ring's
// horizontal and vertical extents. We use the max of the two principal
// diameters to reduce foreshortening bias when the head is slightly turned.
function irisDiameterNorm(landmarks, ring, centreIdx, aspect) {
  const c = landmarks[centreIdx];
  let maxD = 0;
  for (const i of ring) {
    // scale x by aspect so the normalised space is isotropic
    const p = landmarks[i];
    const dx = (p.x - c.x) * aspect;
    const dy = (p.y - c.y);
    const d = 2 * Math.sqrt(dx*dx + dy*dy);   // diameter = 2 × radius
    if (d > maxD) maxD = d;
  }
  return maxD;
}

/**
 * @param {object} params
 *  - landmarks: MediaPipe 478 landmarks (normalised)
 *  - videoWidth, videoHeight: to correct the x/y aspect ratio
 *  - placement: current goggle placement (offset cm, rotation rad, scale)
 *  - smoothedTranslation: {x,y,z} of the head matrix (cm) for vertex estimate
 * @returns {object} measurements in mm + angles in degrees + confidence
 */
export function computeMeasurements({ landmarks, videoWidth, videoHeight, placement }) {
  if (!landmarks || landmarks.length < 478) return null;

  const aspect = videoWidth / videoHeight;

  // 1. Scale factor from iris diameter.
  const leftIrisD  = irisDiameterNorm(landmarks, LEFT_IRIS_RING,  LEFT_EYE_CENTER,  aspect);
  const rightIrisD = irisDiameterNorm(landmarks, RIGHT_IRIS_RING, RIGHT_EYE_CENTER, aspect);
  const avgIrisNorm = (leftIrisD + rightIrisD) / 2;
  if (avgIrisNorm <= 0) return null;
  const mmPerNorm = IRIS_DIAMETER_MM / avgIrisNorm;   // mm per (aspect-corrected) normalised unit

  // helper to measure an aspect-corrected normalised distance between two landmarks
  const measureMM = (a, b) => {
    const dx = (a.x - b.x) * aspect;
    const dy = (a.y - b.y);
    return Math.sqrt(dx*dx + dy*dy) * mmPerNorm;
  };

  const L = landmarks[LEFT_EYE_CENTER];    // subject's left pupil (image right)
  const R = landmarks[RIGHT_EYE_CENTER];   // subject's right pupil (image left)
  const bridge = landmarks[NOSE_BRIDGE_TOP];

  // 2. Total PD — pupil to pupil.
  const pd = measureMM(L, R);

  // 3. Monocular PD — bridge centreline to each pupil (use x-projection).
  const monoL = Math.abs((L.x - bridge.x) * aspect) * mmPerNorm;
  const monoR = Math.abs((R.x - bridge.x) * aspect) * mmPerNorm;

  // 4. Pantoscopic tilt — from the frame pitch (head-local X rotation) plus the
  //    natural face pitch. We read the placement pitch directly; a typical
  //    well-fit frame has 8–12° pantoscopic tilt.
  const placementPitchDeg = (placement?.rotation?.x || 0) * 180 / Math.PI;
  // Base anatomical tilt of where a frame rests on nose + ears ≈ 8°.
  const pantoscopicTilt = 8 + placementPitchDeg;

  // 5. Vertex distance — approximate. The goggle sits at head-local Z = offset.z
  //    (cm) relative to MediaPipe's face origin (near corneal plane). Convert to
  //    mm and clamp to a sane optical range (8–20 mm typical).
  const offZmm = (placement?.offset?.z || 0) * 10;   // cm → mm
  let vertex = 12 + offZmm;                            // 12 mm nominal back-vertex
  vertex = Math.max(5, Math.min(25, vertex));

  // 6. Segment height — vertical distance from pupil to the bottom rim of the
  //    lens. We don't have the rim landmark from the face, so we estimate the
  //    frame's vertical half-height in mm from the target frame width and a
  //    typical lens aspect (lens height ≈ 0.42 × frame width per lens). The
  //    pupil typically sits ~55% up from the bottom of the lens.
  const frameWidthMM = 140 * (placement?.scale || 1);
  const lensHeightMM = 0.42 * (frameWidthMM / 2);     // per-lens height estimate
  const segmentHeight = 0.55 * lensHeightMM;

  // 7. Confidence — based on how symmetric the two iris diameters are (a proxy
  //    for head being square to the camera) and iris size (proxy for distance).
  const irisSymmetry = 1 - Math.abs(leftIrisD - rightIrisD) / Math.max(leftIrisD, rightIrisD);
  const confidence = Math.max(0, Math.min(1, irisSymmetry));

  return {
    pd:               +pd.toFixed(1),
    monoPD_left:      +monoL.toFixed(1),
    monoPD_right:     +monoR.toFixed(1),
    segmentHeight:    +segmentHeight.toFixed(1),
    pantoscopicTilt:  +pantoscopicTilt.toFixed(1),
    vertexDistance:   +vertex.toFixed(1),
    mmPerNorm:        +mmPerNorm.toFixed(4),
    irisDiameterNorm: +avgIrisNorm.toFixed(4),
    confidence:       +confidence.toFixed(2)
  };
}
